import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography } from '@mui/material';
import { debounce } from '@mui/material/utils';
import EntityCard from 'features/IRWorkstation/components/Entity/EntityCard';
import FilterSelect from 'features/IRWorkstation/components/Common/FilterSelect';
import SearchInput from 'features/IRWorkstation/components/Search/SearchInput';
import useEntities from 'features/IRWorkstation/hooks/useEntities';
import { transformEntityToCard } from 'features/IRWorkstation/utils/transformEntityToCard';
import salesforceEntitiesService from 'services/salesforceEntitiesService';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

// "All" options are modeled as an empty value so no filter param is sent
const ALL_TYPES = { label: 'All Types', value: '' };
const ALL_STATUSES = { label: 'All Statuses', value: '' };

const gridStyles = {
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
	gap: '12px',
};

const EntityDashboard = () => {
	const navigate = useNavigate();

	const [searchInput, setSearchInput] = useState('');
	// the value actually sent to the API; debounced so results update as the user
	// types without a request per keystroke
	const [search, setSearch] = useState('');
	const [entityType, setEntityType] = useState('');
	const [status, setStatus] = useState('');
	const [picklistValues, setPicklistValues] = useState(null);

	const debouncedSetSearch = useMemo(
		() => debounce(setSearch, SEARCH_DEBOUNCE_MS),
		[]
	);

	useEffect(() => () => debouncedSetSearch.clear(), [debouncedSetSearch]);

	const handleSearchChange = useCallback(
		(event) => {
			setSearchInput(event.target.value);
			debouncedSetSearch(event.target.value);
		},
		[debouncedSetSearch]
	);

	const {
		entities,
		loading: entitiesLoading,
		loadingMore,
		error: entitiesError,
		total,
		loadMore,
	} = useEntities(PAGE_SIZE, { search, entityType, status });

	// Filter dropdown options come from the Salesforce picklists; a failure leaves
	// the dropdowns with only their "All" option rather than breaking the page.
	useEffect(() => {
		let cancelled = false;

		salesforceEntitiesService
			.getPicklistValues()
			.then((data) => {
				if (!cancelled) setPicklistValues(data);
			})
			.catch((err) => {
				console.error('Failed to load entity filter options:', err);
			});

		return () => {
			cancelled = true;
		};
	}, []);

	const entityTypeOptions = [ALL_TYPES, ...(picklistValues?.entity_type || [])];
	const statusOptions = [ALL_STATUSES, ...(picklistValues?.status || [])];

	const entityCards = entities?.map(transformEntityToCard) || [];

	const scrollContainerRef = useRef(null);

	const handleScroll = useCallback(
		(event) => {
			const el = event.currentTarget;

			// loadMore no-ops while a load is in flight, on error, or once all records are loaded
			if (
				!entitiesError &&
				el.scrollHeight - el.scrollTop <= el.clientHeight + 50
			) {
				loadMore();
			}
		},
		[entitiesError, loadMore]
	);

	// If the loaded cards don't overflow the container, a scroll event can never
	// fire. Keep loading pages until the content overflows; loadMore no-ops once
	// all records are loaded or if an error has occurred.
	useEffect(() => {
		const el = scrollContainerRef.current;

		if (el && !entitiesError && el.scrollHeight <= el.clientHeight) {
			loadMore();
		}
	}, [entityCards.length, entitiesError, loadMore]);

	return (
		<Box
			ref={scrollContainerRef}
			onScroll={handleScroll}
			data-testid='entities-scroll-container'
			sx={{
				flex: 1,
				overflowY: 'auto',
				padding: '16px 20px',
				backgroundColor: 'background.default',
				display: 'flex',
				flexDirection: 'column',
				gap: '14px',
			}}
		>
			{/* page header */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '12px',
				}}
			>
				<Box>
					<Typography sx={{ fontSize: '17px', fontWeight: 700 }}>
						Entity Dashboard
					</Typography>
					<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
						{total ? `${total} entities` : ''}
					</Typography>
				</Box>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
						flexShrink: 0,
					}}
				>
					<FilterSelect
						data-testid='entity-type-filter'
						value={entityType}
						options={entityTypeOptions}
						onChange={setEntityType}
					/>
					<FilterSelect
						data-testid='entity-status-filter'
						value={status}
						options={statusOptions}
						onChange={setStatus}
					/>
					<SearchInput
						data-testid='entity-search-input'
						value={searchInput}
						onChange={handleSearchChange}
						placeholder='Search entities…'
					/>
				</Box>
			</Box>
			{entitiesError ? (
				<EntityCard.Error message='Failed to load entities' />
			) : (
				<Box sx={gridStyles}>
					{entitiesLoading ? (
						<>
							{Array.from({ length: PAGE_SIZE }).map((_, index) => (
								<EntityCard.Skeleton key={index} />
							))}
						</>
					) : entityCards.length === 0 ? (
						<EntityCard.Empty />
					) : (
						<>
							{entityCards.map((card) => (
								<EntityCard
									key={card.id}
									card={card}
									onClick={() => navigate(card.id)}
								/>
							))}
							{loadingMore &&
								Array.from({ length: 4 }).map((_, index) => (
									<EntityCard.Skeleton key={`loading-more-${index}`} />
								))}
						</>
					)}
				</Box>
			)}
		</Box>
	);
};

export default EntityDashboard;
